// The app's navigation, organized by task: one entry per section in the top
// bar, each with the screens (sub-nav tabs) inside it. Every screen has its own
// address. `visible(user)` hides what the user cannot use (see utils/permissions).
// Adding a section (e.g. Marketing: opportunities, contacts and their tasks) is
// one more entry here plus its routes in App.js.
// `short` is the small-screen label (`icon`: an ICONS key shown instead), so the
// section bar fits a phone without scrolling. `badge` is a small tag shown
// after the label (e.g. 'Beta').
import { can } from '../utils/permissions';
import { dashboardPanelsFor } from '../components/DashboardView';

const always = () => true;

export const SECTIONS = [
  {
    key: 'dashboard', label: 'Dashboard', icon: 'dashboard', path: '/dashboard',
    visible: (u) => dashboardPanelsFor(u).length > 0,
    items: [],
  },
  {
    key: 'time', label: 'Time', path: '/time/timesheets',
    visible: always,
    items: [],
  },
  {
    key: 'expenses', label: 'Expenses', short: 'Exp', path: '/expenses',
    visible: always,
    items: [],
  },
  {
    key: 'jobs', label: 'Jobs', path: '/jobs',
    visible: (u) => can(u, 'createInvoices') || can(u, 'invoiceApproval') || can(u, 'budget'),
    items: [
      { key: 'list', label: 'Job List', short: 'List', path: '/jobs/list', visible: always },
      { key: 'invoicing', label: 'Create Invoices', short: 'Invoicing', path: '/jobs/invoicing', visible: (u) => can(u, 'createInvoices') },
      { key: 'approval', label: 'Invoice Approval', short: 'Approval', path: '/jobs/approval', visible: (u) => can(u, 'invoiceApproval') },
      { key: 'budget', label: 'Budget', path: '/jobs/budget', visible: (u) => can(u, 'budget') },
    ],
  },
  {
    key: 'receivables', label: 'Receivables', short: 'A/R', path: '/receivables',
    visible: (u) => can(u, 'receivables'),
    items: [],
  },
  {
    key: 'projections', label: 'Projections', short: 'Prjtns', path: '/projections',
    visible: (u) => can(u, 'projections') || can(u, 'projectionsAdmin'),
    items: [
      { key: 'projections', label: 'Projections', path: '/projections', visible: (u) => can(u, 'projections') },
      { key: 'admin', label: 'Projections Admin', short: 'Admin', path: '/projections/admin', visible: (u) => can(u, 'projectionsAdmin') },
    ],
  },
  {
    key: 'analytics', label: 'Analytics', short: 'Stats', badge: 'Beta', path: '/analytics',
    visible: always,
    items: [
      { key: 'billable', label: 'Billable %', path: '/analytics/billable', visible: always },
      { key: 'reports', label: 'Reports', path: '/analytics/reports', visible: (u) => can(u, 'reports') },
    ],
  },
];

// Settings is reached from the gear icon, not the section bar.
export const SETTINGS_ITEMS = [
  { key: 'mine', label: 'My Settings', path: '/settings', visible: always },
  { key: 'admin', label: 'Admin', path: '/settings/admin', visible: (u) => can(u, 'admin') },
];

// The tabs of a job page (/jobs/:jobNo/:tab). `billing` tabs are left off an
// overhead job, which is never invoiced.
export const JOB_TABS = [
  { key: 'budget', label: 'Budget', visible: (u) => can(u, 'budget') },
  { key: 'entries', label: 'Entries', visible: (u) => can(u, 'entries') },
  { key: 'invoicing', label: 'Create Invoices', short: 'Invoicing', billing: true, visible: (u) => can(u, 'createInvoices') },
  { key: 'approval', label: 'Invoice Approval', short: 'Approval', billing: true, visible: (u) => can(u, 'invoiceApproval') },
  { key: 'receivables', label: 'Receivables', short: 'A/R', billing: true, visible: (u) => can(u, 'receivables') },
];
// The tabs a user sees on a job page.
export const jobTabsFor = (user, isOverhead) =>
  visibleItems(user, JOB_TABS).filter((t) => !(isOverhead && t.billing));

// Jobs-section paths that are screens, not a job number.
export const JOB_SCREENS = ['list', 'invoicing', 'approval', 'budget'];

export const visibleSections = (user) => SECTIONS.filter((s) => s.visible(user));
export const visibleItems = (user, items) => (items || []).filter((i) => i.visible(user));

// The section a path belongs to (by its first segment), or null.
export function sectionForPath(pathname) {
  const first = '/' + (pathname.split('/')[1] || '');
  return SECTIONS.find((s) => s.path === first || s.path.startsWith(first + '/')) || null;
}

// A section's landing address: its first screen the user can see.
export function sectionLanding(user, section) {
  const items = visibleItems(user, section.items);
  return items.length ? items[0].path : section.path;
}

// Where "/" (and the brand) goes: the Dashboard if the user has panels,
// otherwise the timesheet list.
export const homePath = (user) => (dashboardPanelsFor(user).length > 0 ? '/dashboard' : '/time/timesheets');
